import { useState, useEffect } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, DetailRow, ExcelExport, Toolbar, Filter } from '@syncfusion/ej2-react-grids';
import Badge from '../components/Badge';
import WorkflowDetail from '../components/WorkflowDetail';
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ClearIcon from '@mui/icons-material/Clear';

const DEFAULT_ICON_SIZE = 12;

const WORKFLOW_STATUS = [
    'UNDEFINED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'SUSPENDED'
]

const WORKFLOW_STATUS_COLOR = [
    'default',
    'primary',
    'success',
    'error',
    'warning'
]

const WORKFLOW_STATUS_ICON = [
    <HelpOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <CheckCircleOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />
]

export default function WorkflowAdmin() {
    const [gridData, setGridData] = useState({ result: [], count: 0 });
    const [pageSettings, setPageSettings] = useState({
        pageSize: 20,
        currentPage: 1,
        totalRecordsCount: 0
    });
    const [filters, setFilters] = useState({});

    let grid;

    const getWorkflowContexts = async () => {
        try {
            let url = `/api/workflowadmin?page=${pageSettings.currentPage}&pageSize=${pageSettings.pageSize}`;
            if (Object.keys(filters || {}).length) {
                url += '&' + new URLSearchParams(filters).toString();
            }
            const response = await fetch(url);
            if (response.ok) {
                const result = await response.json();

                setGridData({
                    result: formatData(result.items),
                    count: result.total
                });

            }
        } catch (error) {
            console.error('Error fetching workflow contexts:', error);
        }
    };

    useEffect(() => {
        getWorkflowContexts();
    }, []);

    useEffect(() => {
        getWorkflowContexts();
    }, [filters, pageSettings]);

    const formatData = (data) => {
        return data.map(item => ({
            ...item,
            createdAtDate: <span>{formatDate(item.createdAt)} <b>{formatTime(item.createdAt)}</b></span>,
            updatedAtDate: <span>{formatDate(item.updatedAt)} <b>{formatTime(item.updatedAt)}</b></span>,
        }));
    };

    const formatDate = (dateString) => {
        const date = new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = date.getDate().toString().padStart(2, '0');
        const yyyymmdd = `${year}-${month}-${day}`;
        return yyyymmdd;
    }

    const formatTime = (dateString) => {
        const date = new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const hhmm = `${hours}:${minutes}:${seconds}`;
        return hhmm;
    }

    const toolbarClick = async (args) => {
        if (grid && args.item.id === 'Grid_excelexport') {
            try {
                let url = `/api/workflowadmin?all=true`;
                url += '&' + new URLSearchParams(filters).toString();
                const response = await fetch(url);
                const result = await response.json();
                grid.showSpinner();
                grid.excelExport({ exportType: 'AllPages', dataSource: result });
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                grid.hideSpinner();
            }
        }
    }
    const excelExportComplete = () => {
        grid.hideSpinner();
    }

    const handleActionBegin = (args) => {
        if (args.requestType === 'paging') {
            setPageSettings(prev => ({
                ...prev,
                currentPage: args.currentPage
            }));
        }
        if (args.requestType === 'filtering') {

            const activeFilters = grid?.filterSettings?.columns || [];

            const colsToFilter = ['workflowName', 'userId'];
            const newFilters = {};

            activeFilters.forEach(col => {
                if (colsToFilter.includes(col.field) && col.value) {
                    newFilters[col.field] = col.value;
                }
            });

            setFilters(newFilters);
        }
    };

    const onTypeFilterChange = (args) => {
        if (args.value === -1) {
            setFilters(prev => {
                const newFilters = { ...prev };
                delete newFilters.status;
                return newFilters;
            });
        } else {
            setFilters(prev => {
                const newFilters = { ...prev };
                newFilters.status = args.value;
                return newFilters;
            });
        }
    };

    const filterStartDateTemplate = () => {
        return (
            <DateRangePickerComponent
                placeholder="Select a date range"
                change={(args) => {
                    if (args.value && args.value.length === 2) {
                        const startDate = args.value[0];
                        const endDate = args.value[1];
                        setFilters(prev => ({
                            ...prev,
                            createdAtStart: startDate.toISOString(),
                            createdAtEnd: endDate.toISOString()
                        }));
                    } else {
                        setFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters.createdAtStart;
                            delete newFilters.createdAtEnd;
                            return newFilters;
                        });
                    }
                }}
            />
        );
    }

    const filterUpdatedDateTemplate = () => {
        return (
            <DateRangePickerComponent
                placeholder="Select a date range"
                change={(args) => {
                    if (args.value && args.value.length === 2) {
                        const startDate = args.value[0];
                        const endDate = args.value[1];
                        setFilters(prev => ({
                            ...prev,
                            updatedAtStart: startDate.toISOString(),
                            updatedAtEnd: endDate.toISOString()
                        }));
                    } else {
                        setFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters.updatedAtStart;
                            delete newFilters.updatedAtEnd;
                            return newFilters;
                        });
                    }
                }}
            />
        );
    }

    const refresh = (id) => {
        if (id) {
            setFilters(prev => ({
                ...prev,
                retryHistory: id
            }));
        } else {
            getWorkflowContexts();
        }
    }

    const filterStatusTemplate = () => {
        const filterDropData = [
            { Status: 'All', Value: -1 },
            { Status: 'Undefined', Value: 0 },
            { Status: 'Running', Value: 1 },
            { Status: 'Completed', Value: 2 },
            { Status: 'Failed', Value: 3 },
            { Status: 'Suspended', Value: 4 }
        ];

        return (
            <DropDownListComponent
                dataSource={filterDropData}
                fields={{ text: 'Status', value: 'Value' }}
                value={filterDropData[0].Value}
                change={onTypeFilterChange}
            />
        );
    };

    const dataStateChange = (state) => {
        document.querySelector('.e-spinner-pane').style.display = "none";
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                height: '100%',
                width: '100%'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, width: '100%' }}>
                <Button size='small' variant='outlined' onClick={() => refresh()} sx={{ alignSelf: 'flex-start', mb: 1 }}>Refresh</Button>
                {filters.retryHistory &&
                    <Box sx={{ alignSelf: 'flex-start', mb: 1, mr: 5 }} >
                        History workflow context ID:
                        <div style={{ border: 'solid 1px black' }}>{filters.retryHistory} <IconButton onClick={() => setFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters.retryHistory;
                            return newFilters;
                        })}><ClearIcon /></IconButton> </div>
                    </Box>
                }
            </Box>
            <GridComponent
                id='Grid'
                ref={g => grid = g}
                dataSource={gridData}
                pageSettings={pageSettings}
                actionBegin={handleActionBegin}
                height='60vh'
                detailTemplate={(props) => <WorkflowDetail {...props} refresh={refresh} />}
                toolbar={['ExcelExport']}
                toolbarClick={toolbarClick}
                excelExportComplete={excelExportComplete}
                dataStateChange={dataStateChange}
                allowPaging
                allowExcelExport
                allowFiltering
            >
                <ColumnsDirective>
                    <ColumnDirective field='workflowName' headerText='Name' width='50' template={(props) => (
                        <Badge type='none'>{props.workflowName}<b>(v{props.workflowVersion})</b></Badge>
                    )} />
                    <ColumnDirective field='status' headerText='Status' width='60' filterTemplate={filterStatusTemplate} template={(props) => (
                        <Badge type={WORKFLOW_STATUS_COLOR[props.status]} icon={WORKFLOW_STATUS_ICON[props.status]}>
                            {WORKFLOW_STATUS[props.status]}
                        </Badge>
                    )} />
                    <ColumnDirective field='step' allowFiltering={false} headerText='Last Step' width='80' />
                    <ColumnDirective field='userId' headerText='User' width='100' />
                    <ColumnDirective field='createdAt' type='date' format='yMd' filterTemplate={filterStartDateTemplate} headerText='Start Date' width='80' template={(props) => (
                        <Badge type='default' icon={<CalendarMonthIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />}>{props.createdAtDate}</Badge>
                    )} />
                    <ColumnDirective field='updatedAt' type='date' format='yMd' filterTemplate={filterUpdatedDateTemplate} headerText='Last Update' width='80' template={(props) => (
                        <Badge type='default' icon={<CalendarMonthIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />}>{props.updatedAtDate}</Badge>
                    )} />
                    <ColumnDirective field='parentWorkflowContextId' allowFiltering={false} headerText='' width={50} template={(props) => (
                        <>{props.parentWorkflowContextId ? <ReplayIcon /> : <></>}</>
                    )} />
                </ColumnsDirective>
                <Inject services={[Page, DetailRow, ExcelExport, Toolbar, Filter]} />
            </GridComponent>
        </Box>
    );
}
